import * as React from 'react'
import { HeadContent, Outlet, Scripts, createRootRouteWithContext, type ErrorComponentProps } from '@tanstack/react-router'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@mui/material/styles'
import { useTheme } from '../lib/theme'
import { PageViewLogger } from '../components/PageViewLogger'
import { Layout } from '../components/layout'
import { getSchoolTheme } from '../lib/schoolThemes'
import { adjustColor, hexToRgbComponents } from '../lib/util'

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, minimum-scale=1.0' },
      { rel: 'icon', href: '/favicon.ico' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { title: 'Unilytics — Real Grade Data for College Students' },
      { name: 'description', content: 'Search grade distributions, compare professors, and make smarter course decisions at your university.' },
      { property: 'og:title', content: 'Unilytics — Real Grade Data for College Students' },
      { property: 'og:description', content: 'Search grade distributions, compare professors, and make smarter course decisions at your university.' },
      { property: 'og:image', content: '/img/og-placeholder.png' },
      { property: 'og:url', content: 'https://unilytics.app' },
    ]
  }),
  component: RootComponent,
})

import errorStyles from '../styles/Error.module.scss'

import '../styles/tailwind.css'
import '../styles/new.css'
import 'bootstrap/dist/css/bootstrap-grid.css'
import 'bootstrap/dist/css/bootstrap-utilities.css'
import 'nprogress/nprogress.css'
import '../styles/nprogress-custom.scss'
import '../styles/globals.scss'
import '../styles/colors.scss'
import '../styles/syntax-highlighting.scss'

function RootComponent() {
  const theme = useTheme();
  const context = Route.useRouteContext();

  React.useEffect(() => {
    const schoolTheme = getSchoolTheme('uh')
    const root = document.documentElement
    root.style.setProperty('--school-primary', schoolTheme.primaryColor)
    root.style.setProperty('--school-secondary', schoolTheme.secondaryColor)
    root.style.setProperty('--school-primary-rgb', hexToRgbComponents(schoolTheme.primaryColor))
    root.style.setProperty('--school-primary-dark', adjustColor(schoolTheme.primaryColor, -20))
    root.style.setProperty('--school-primary-light', `rgba(${hexToRgbComponents(schoolTheme.primaryColor)}, 0.08)`)
  }, [])

  return (
    <>
    <HeadContent />
    <QueryClientProvider client={context.queryClient}>
    <ThemeProvider theme={theme}>
      <PageViewLogger />
      <Layout>
<Outlet />
      </Layout>
      <TanStackDevtools
        config={{
          position: 'bottom-right',
        }}
        plugins={[
          {
            name: 'Tanstack Router',
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
    </ThemeProvider>
    </QueryClientProvider>
    </>
  )
}

function ErrorComponent({ error }: ErrorComponentProps) {
  return (
    <div className={errorStyles.error}>
      <div>
        <h1>{error.name}</h1>
        <div className={errorStyles.title}>
          <h2>{error.message}</h2>
        </div>
      </div>
    </div>
  )
}
